
import { Archive, FileEdit, FileText, PenLine } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsCms } from '@/hooks/use-analytics-cms';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PainelAnalyticsCmsPage() {
  const pageText = usePainelText('analytics_cms');
  const { data, loading } = useAnalyticsCms();

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Estado atual</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Publicados"
          value={loading ? '—' : formatInt(data.published)}
          icon={FileText}
          variant="sky"
        />
        <PainelStatCard
          title="Atualizados (30d)"
          value={loading ? '—' : formatInt(data.updated)}
          icon={PenLine}
          variant="cyan"
        />
        <PainelStatCard
          title="Rascunhos"
          value={loading ? '—' : formatInt(data.draft + data.pendingReview)}
          icon={FileEdit}
          description={`${formatInt(data.pendingReview)} em revisão`}
          variant="indigo"
        />
        <PainelStatCard
          title="Arquivados"
          value={loading ? '—' : formatInt(data.archived)}
          icon={Archive}
          description={`Última atividade: ${formatDate(data.lastActivityAt)}`}
          variant="violet"
        />
      </div>

      <AnalyticsPanel title="Guias por editor" subtitle="Publicações e atualizações">
        <div className={cn(painelTableShellClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-separate border-spacing-0">
              <thead>
                <tr className="bg-white/[0.03] font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
                  <th className="px-4 py-3 text-left">Editor</th>
                  <th className="px-4 py-3 text-right">Publicados</th>
                  <th className="px-4 py-3 text-right">Atualizações</th>
                  <th className="px-4 py-3 text-right">Última atividade</th>
                </tr>
              </thead>
              <tbody className="font-inter text-sm">
                {data.byEditor.map((row) => (
                  <tr key={String(row.userId)} className="text-blue-100/90">
                    <td className="border-t border-white/[0.06] px-4 py-3">{row.name}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.published)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.updated)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right text-slate-400">
                      {formatDate(row.lastActivityAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && data.byEditor.length === 0 ? (
            <p className="px-4 py-3 font-inter text-xs text-slate-400/80">Sem editores com guias associados.</p>
          ) : null}
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
